import 'react-app-polyfill/stable';
import 'react-app-polyfill/ie9';
import 'custom-event-polyfill';

import { Constants, ModelManager } from '@adobe/aem-spa-page-model-manager';
import { createBrowserHistory } from 'history';
import React from 'react';
import { render } from 'react-dom';
import { AuthServiceProvider, AuthGate } from '@trp-ta-nitro/auth-react';
import { ConnectedRouter } from 'connected-react-router';
import store, { history } from './redux/configure-store';
import App from './App';
import LocalDevModelClient from './LocalDevModelClient';
import './components/import-components';
import './index.css';
import {Provider} from "react-redux";
import { useSelector } from "react-redux";
import { selectApiKey } from "./redux/app-config/selectors";
import AppConfigLoader from './AppConfigLoader';
import checkAndResetPageModel, {localPageModel} from 'page-model.local';
import {getDomain} from "./utils/api";

// const modelManagerOptions = {};
// if(process.env.REACT_APP_PROXY_ENABLED) {
//     modelManagerOptions.modelClient = new LocalDevModelClient(process.env.REACT_APP_API_HOST);
// }
const rootElement = document.getElementById('spa-root');
const isLocalhost = window && window.location.hostname.toLowerCase().indexOf('localhost') !== -1;
let isAuthorEnvironment = false;
const isAuthorEnvironmentLocal = isLocalhost && window && window.location.port !== `${process.env.REACT_APP_PORT}`;
const configs = isLocalhost
  ? [
      `http://localhost.troweprice.com:${process.env.REACT_APP_PORT}/baseConfig.json`,
      `http://localhost.troweprice.com:${process.env.REACT_APP_PORT}/envConfig.json`,
    ]
  : [
        '/workplace/en/participant/profile/baseConfig.json', 
        '/workplace/en/participant/profile/envConfig.json'
    ];

const AuthenticatedApp = (props: any) => {
    const { pageModel } = props;
    const apiKey = useSelector(selectApiKey);

    if (!apiKey) {
        throw new Error(
            "apiKey is unavailable. AuthenticatedApp likely loaded before reduxStore.app-config was initialized."
        );
    }

    // the auth options here lean heavily on the defaults, any other configuration could be
    // pulled in:
    //   - static configuration here
    //   - via the app state (where the apiKey is currently coming from)
    //   - a separate fetch here to specific config
    const authOptions = {
        apiKey,
        types: {
            // enable external and internal both, no extra options are required so none are passed
            external: {},
            internal: {}
        }
    };
    // const history = createBrowserHistory();

    return (
        <AuthServiceProvider options={authOptions}>
            <AuthGate>
                    <App
                        history={history}
                        cqChildren={pageModel[Constants.CHILDREN_PROP]}
                        cqItems={pageModel[Constants.ITEMS_PROP]}
                        cqItemsOrder={pageModel[Constants.ITEMS_ORDER_PROP]}
                        cqPath={pageModel[Constants.PATH_PROP]}
                        locationPathname={window.location.pathname}
                    />
            </AuthGate>
        </AuthServiceProvider>
    );
};

const ConfiguredAndAuthenticatedApp = (props: any) => {
    const { pageModel } = props;
    return (
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <AppConfigLoader configs={configs}>
                    <AuthenticatedApp pageModel={pageModel} />
                </AppConfigLoader>
            </ConnectedRouter>
        </Provider>
    );
};

const isProfileLocalizeEnabled = () => {
    return window.location.pathname.indexOf('/workplace/en/participant/profile') >= 0;
};

const RootComponent = (props: any) => {
    const history = createBrowserHistory();
    const { pageModel } = props;
    return (
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <App
                    history={history}
                    cqChildren={pageModel[Constants.CHILDREN_PROP]}
                    cqItems={pageModel[Constants.ITEMS_PROP]}
                    cqItemsOrder={pageModel[Constants.ITEMS_ORDER_PROP]}
                    cqPath={pageModel[Constants.PATH_PROP]}
                    locationPathname={window.location.pathname}
                />
            </ConnectedRouter>
        </Provider>
    );
  };


function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => {
            console.log('Loaded script: ', src);
            resolve();
        };
        script.onerror = (e) => {
            console.error('Failed to load script:', src, e);
            reject(e);
        };
        document.body.appendChild(script);
    });
}

function loadStyle(href: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`link[href="${href}"]`)) {
            resolve();
            return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = () => {
            console.log('Loaded style:', href);
            resolve();
        };
        link.onerror = (e) => {
            console.error('Failed to load style:', href, e);
            reject(e);
        };
        document.head.appendChild(link);
    });
}

const normalizeProfileLanguage = (language: string | null | undefined) => {
    const lang = (language || '').toLowerCase();

    if (lang === 'es' || lang === 'es-us') {
        return 'es';
    }

    return 'en';
};

const getProfileSelectedLanguage = () => {
    return normalizeProfileLanguage(localStorage.getItem('i18nextLng'));
};

const applyProfileLanguage = (language: string | null | undefined) => {
    const lang = normalizeProfileLanguage(language);
    const localizeLang = lang === 'es' ? 'es-US' : 'en';

    localStorage.setItem('i18nextLng', lang);
    localStorage.setItem('ljs-lang', localizeLang);
    document.documentElement.lang = localizeLang;

    if ((window as any).Localize && typeof (window as any).Localize.setLanguage === 'function') {
        (window as any).Localize.setLanguage(localizeLang);
    }

    if ((window as any).Localize && typeof (window as any).Localize.translate === 'function') {
        (window as any).Localize.translate();
    }

    if ((window as any).Localize && typeof (window as any).Localize.translateImmediate === 'function') {
        (window as any).Localize.translateImmediate();
    }

    window.setTimeout(() => {
        window.location.reload();
    }, 100);
};

const addProfileLanguageBridge = () => {
    if (window.location.pathname.indexOf('/participant/profile') < 0) {
        return;
    }

    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const languateOption = target && typeof target.closest === 'function' 
            ? target.closest('#language1, #language2') as HTMLElement : null;

        const languageSelector = document.getElementById('language-selector-uid');

        if (!languateOption || !languageSelector || !languageSelector.contains(languateOption)) {
            return;
        }

        const selectedLanguage = languateOption.id === 'language2' ? 'es' : 'en';

        localStorage.setItem('profileSelectedLanguage', selectedLanguage);
    }, true);

    if ((window as any).i18next) {
        return;
    }

    (window as any).i18next = {
        changeOTCLanguage: (language: string) => {
            const selectedLanguage = localStorage.getItem('profileSelectedLanguage') || language;
            localStorage.removeItem('profileSelectedLanguage');
            applyProfileLanguage(selectedLanguage);
        },
        changeLanguage: (language: string) => {
            const selectedLanguage = localStorage.getItem('profileSelectedLanguage') || language;
            localStorage.removeItem('profileSelectedLanguage');
            applyProfileLanguage(selectedLanguage);
        }
    };
};

const enableProfileLocalize = () => {
    const selectedLanguage = getProfileSelectedLanguage();

    if (selectedLanguage !== 'es') {
        document.body.classList.remove('localizejs');
        document.documentElement.lang = 'en';

        localStorage.setItem('ljs-lang', 'en');

        if ((window as any).Localize && typeof (window as any).Localize.setLanguage === 'function') {
            (window as any).Localize.setLanguage('en');

            if (typeof (window as any).Localize.untranslate === 'function') {
                (window as any).Localize.untranslate();
            }
        }

        return;
    }

    document.body.classList.add('localizejs');
    document.documentElement.lang = 'es-US';
    localStorage.setItem('ljs-lang', 'es-US');

    const runLocalize = () => {
        if ((window as any).Localize && typeof (window as any).Localize.setLanguage === 'function') {
            (window as any).Localize.setLanguage('es-US');

            if (typeof (window as any).Localize.translate === 'function') {
                (window as any).Localize.translate();
            }

            if (typeof (window as any).Localize.translateImmediate === 'function') {
                (window as any).Localize.translateImmediate();
            }

            console.log('===> Profile Localize enabled');
        } else {
            console.warn('===> Localize not ready yet');
        }
    };

    runLocalize();
    setTimeout(runLocalize, 1000);
};

document.addEventListener('DOMContentLoaded', () => {
    addProfileLanguageBridge();
    enableProfileLocalize();
    console.log('===> index.tsx DOMContentLoaded fired');

    fetch('/content/workplace/en/participant/profile.model.json').then(response => {
        if (response.headers.get('X-Vhost') === 'author') {
          isAuthorEnvironment = true;
        }
        return;
      });

    ModelManager.initialize({ path: '/content/workplace/en/participant/profile' }).then((pageModel: any) => {
        //comment-out below line while aem author deployment
        pageModel = checkAndResetPageModel(pageModel);

        if (isLocalhost || isAuthorEnvironmentLocal || isAuthorEnvironment) {
            render(<RootComponent pageModel={pageModel} />, rootElement);
        } else {
            if(window.location.href.indexOf('preference.html') > -1) {
                window.location = `${awsWorkplaceDomain}/workplace/en/participant/profile/index.html`;
            } else {
                render(<ConfiguredAndAuthenticatedApp pageModel={pageModel}/>, rootElement);
            }
        }
    });
});
